<template>
  <div class="contact-sync-dashboard">
    <div v-if="loading && !syncing" class="loading">
      <p>Loading active clients and contacts...</p>
    </div>

    <div v-else-if="error && !syncing" class="error">
      <p>{{ error }}</p>
      <button @click="loadData" class="btn-retry">Retry</button>
    </div>

    <!-- Sync Progress Bar -->
    <div v-if="syncing" class="sync-progress-container">
      <div class="sync-progress-header">
        <h3>Syncing Contacts</h3>
        <button @click="cancelSync" class="btn-cancel">Cancel</button>
      </div>
      <div class="sync-progress-bar-container">
        <div class="sync-progress-bar" :style="{ width: progressPercentage + '%' }"></div>
      </div>
      <div class="sync-progress-info">
        <p class="sync-status">{{ syncStatus || 'Starting sync...' }}</p>
        <p class="sync-details">{{ syncCurrent }} of {{ syncTotal }} contacts</p>
        <p class="sync-details">You can safely leave this page; the sync will continue in the background.</p>
        <p v-if="syncContactName" class="sync-contact-name">
          <strong>Currently syncing:</strong> {{ syncContactName }}
        </p>
        <p v-else-if="syncing" class="sync-contact-name">
          <strong>Preparing sync...</strong>
        </p>
      </div>
      <div v-if="syncErrors.length > 0" class="sync-errors">
        <h4>Errors:</h4>
        <ul>
          <li v-for="(error, index) in syncErrors" :key="index">{{ error }}</li>
        </ul>
      </div>
    </div>

    <!-- Sync Summary Modal -->
    <div v-if="showSyncSummary" class="modal-overlay" @click.self="closeSyncSummary">
      <div class="modal-content sync-summary-modal">
        <div class="modal-header">
          <h2>Sync Summary</h2>
          <button @click="closeSyncSummary" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="summary-stats">
            <div class="summary-stat success">
              <div class="stat-label">Successfully Synced</div>
              <div class="stat-value">{{ syncSummary.synced || 0 }}</div>
            </div>
            <div class="summary-stat error" v-if="syncSummary.errors > 0">
              <div class="stat-label">Errors</div>
              <div class="stat-value">{{ syncSummary.errors || 0 }}</div>
            </div>
            <div class="summary-stat excluded" v-if="syncSummary.excluded > 0">
              <div class="stat-label">Excluded</div>
              <div class="stat-value">{{ syncSummary.excluded || 0 }}</div>
            </div>
          </div>
          
          <div v-if="syncSummary.errors > 0 && syncErrors.length > 0" class="summary-errors">
            <h3>Errors:</h3>
            <ul>
              <li v-for="(error, index) in syncErrors" :key="index">{{ error }}</li>
            </ul>
          </div>
          
          <div v-if="syncSummary.excluded > 0 && syncSummary.excludedContacts" class="summary-excluded">
            <h3>Excluded Contacts:</h3>
            <p>{{ syncSummary.excluded }} contact(s) were excluded because they are missing required fields (email, phone, firstName, or lastName).</p>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeSyncSummary" class="btn-primary">Close</button>
        </div>
      </div>
    </div>

    <div v-else class="dashboard-content">
      <!-- Total Active Clients Count -->
      <div 
        class="total-count-card"
        :class="{ active: isTotalActiveClientsActive }"
        @click="selectFilter('all')"
      >
        <div class="total-count-label">Total Active Clients</div>
        <div class="total-count-value">{{ totalActiveClients }}</div>
        <div class="contact-count">{{ totalContacts }} Contacts</div>
      </div>

      <!-- Breakdown Cards - Order: Individuals, Companies, Head Offices, Sites -->
      <div class="breakdown-cards">
        <div 
          class="metric-card"
          :class="{ active: selectedFilters.includes('individuals') }"
          @click="toggleFilter('individuals')"
        >
          <div class="metric-label">Individuals</div>
          <div class="metric-value">{{ metrics.individuals }}</div>
          <div class="contact-count">{{ metrics.individualContacts }} Contacts</div>
        </div>

        <div 
          class="metric-card"
          :class="{ active: selectedFilters.includes('companies') }"
          @click="toggleFilter('companies')"
        >
          <div class="metric-label">Companies</div>
          <div class="metric-value">{{ metrics.companies }}</div>
          <div class="contact-count">{{ metrics.companyContacts }} Contacts</div>
        </div>

        <div 
          class="metric-card"
          :class="{ active: selectedFilters.includes('headOffices') }"
          @click="toggleFilter('headOffices')"
        >
          <div class="metric-label">Head Offices</div>
          <div class="metric-value">{{ metrics.headOffices }}</div>
          <div class="contact-count">{{ metrics.headOfficeContacts }} Contacts</div>
        </div>

        <div 
          class="metric-card"
          :class="{ active: selectedFilters.includes('sites') }"
          @click="toggleFilter('sites')"
        >
          <div class="metric-label">Sites</div>
          <div class="metric-value">{{ metrics.sites }}</div>
          <div class="contact-count">{{ metrics.siteContacts }} Contacts</div>
        </div>
      </div>

      <!-- Auto Sync Settings (only show during initial sync) -->
      <div v-if="!initialSyncAt" class="sync-settings-container">
        <h4>Auto Sync Settings</h4>
        <p class="sync-settings-description">Select which contact types to sync (matches filter cards above):</p>
        <div class="sync-settings-checkboxes">
          <label class="sync-setting-checkbox">
            <input 
              type="checkbox" 
              v-model="syncSettings.syncIndividual"
              @change="updateSyncSettingsFromCheckbox('individuals', syncSettings.syncIndividual)"
              :disabled="syncing"
            />
            <span>Sync Individuals ({{ metrics.individualContacts }} contacts)</span>
          </label>
          <label class="sync-setting-checkbox">
            <input 
              type="checkbox" 
              v-model="syncSettings.syncCompany"
              @change="updateSyncSettingsFromCheckbox('companies', syncSettings.syncCompany)"
              :disabled="syncing"
            />
            <span>Sync Companies ({{ metrics.companyContacts }} contacts)</span>
          </label>
          <label class="sync-setting-checkbox">
            <input 
              type="checkbox" 
              v-model="syncSettings.syncSite"
              @change="updateSyncSettingsFromCheckbox('sites', syncSettings.syncSite)"
              :disabled="syncing"
            />
            <span>Sites and Head Offices ({{ metrics.siteContacts + metrics.headOfficeContacts }} contacts)</span>
          </label>
        </div>
        <p v-if="!syncSettings.syncIndividual && !syncSettings.syncCompany && !syncSettings.syncSite" class="sync-settings-warning">
          Please select at least one contact type to sync.
        </p>
      </div>

      <!-- Sync Button (Top) -->
      <div class="sync-button-container">
        <!-- Initial Sync Message -->
        <p v-if="!initialSyncAt" class="sync-action-message">
          Click to Perform the Initial Sync and activate auto sync.
        </p>
        <!-- Manual Sync Message -->
        <p v-if="initialSyncAt" class="sync-action-message">
          Click to save auto sync settings and perform manual sync
        </p>
        
        <button 
          class="sync-button" 
          @click="syncContacts"
          :disabled="syncing || (!syncSettings.syncIndividual && !syncSettings.syncCompany && !syncSettings.syncSite && !initialSyncAt)"
        >
          <span v-if="!initialSyncAt">Sync {{ validContactsCount }} Contacts</span>
          <span v-else>Perform Manual sync on {{ validContactsCount }} contacts</span>
        </button>
        <p v-if="excludedContactsCountComputed > 0" class="excluded-contacts-message">
          {{ excludedContactsCountComputed }} Contact{{ excludedContactsCountComputed !== 1 ? 's' : '' }} Excluded
        </p>
      </div>

      <!-- Contacts Tables -->
      <div class="contacts-tables-section">
        <!-- Individual Contacts Table -->
        <template v-if="groupedContacts.individuals && groupedContacts.individuals.length > 0">
          <div class="contact-table-section">
            <h4 class="section-title">Individual Contacts</h4>
            <div class="table-container">
              <table class="contacts-table">
                <thead>
                  <tr>
                    <th class="checkbox-header">
                      <input 
                        type="checkbox" 
                        :checked="areAllContactsSelected('individuals')"
                        @change="toggleAllContacts('individuals', $event.target.checked)"
                      />
                    </th>
                    <th v-for="column in getTableColumns('individuals')" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="contact in groupedContacts.individuals" :key="contact.uuid" class="contact-row">
                    <td class="checkbox-cell">
                      <input 
                        type="checkbox" 
                        :checked="isContactSelected(contact.uuid)"
                        @change="toggleContactSelection(contact.uuid, $event.target.checked)"
                      />
                    </td>
                    <td v-for="column in getTableColumns('individuals')" :key="column.key">
                      {{ getContactValue(contact, column.key) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <!-- Company Contacts Table -->
        <template v-if="groupedContacts.companies && groupedContacts.companies.length > 0">
          <div class="contact-table-section">
            <h4 class="section-title">Company Contacts</h4>
            <div class="table-container">
              <table class="contacts-table">
                <thead>
                  <tr>
                    <th class="checkbox-header">
                      <input 
                        type="checkbox" 
                        :checked="areAllContactsSelected('companies')"
                        @change="toggleAllContacts('companies', $event.target.checked)"
                      />
                    </th>
                    <th v-for="column in getTableColumns('companies')" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="contact in groupedContacts.companies" :key="contact.uuid" class="contact-row">
                    <td class="checkbox-cell">
                      <input 
                        type="checkbox" 
                        :checked="isContactSelected(contact.uuid)"
                        @change="toggleContactSelection(contact.uuid, $event.target.checked)"
                      />
                    </td>
                    <td v-for="column in getTableColumns('companies')" :key="column.key">
                      {{ getContactValue(contact, column.key) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <!-- Head Office Contacts (with Site Contacts nested) -->
        <template v-if="groupedContacts.headOfficesGrouped && Object.keys(groupedContacts.headOfficesGrouped).length > 0">
          <div 
            v-for="(group, headOfficeUuid) in groupedContacts.headOfficesGrouped" 
            :key="headOfficeUuid"
            class="contact-table-section"
          >
            <h4 class="section-title">{{ group.headOfficeName }} (Head Office)</h4>
            
            <!-- Head Office Contacts Table -->
            <template v-if="group.headOfficeContacts.length > 0">
              <div class="table-container">
                <table class="contacts-table">
                  <thead>
                    <tr>
                      <th class="checkbox-header">
                        <input 
                          type="checkbox" 
                          :checked="areAllContactsSelectedForGroup(group.headOfficeContacts)"
                          @change="toggleAllContactsForGroup(group.headOfficeContacts, $event.target.checked)"
                        />
                      </th>
                      <th v-for="column in getTableColumns('headOffices')" :key="column.key">{{ column.label }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="contact in group.headOfficeContacts" :key="contact.uuid" class="contact-row">
                      <td class="checkbox-cell">
                        <input 
                          type="checkbox" 
                          :checked="isContactSelected(contact.uuid)"
                          @change="toggleContactSelection(contact.uuid, $event.target.checked)"
                        />
                      </td>
                      <td v-for="column in getTableColumns('headOffices')" :key="column.key">
                        {{ getContactValue(contact, column.key) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

            <!-- Site Contacts Table (nested under Head Office) -->
            <template v-if="group.siteContacts.length > 0">
              <div class="nested-table-container">
                <h5 class="nested-section-title">Site Contacts</h5>
                <div class="table-container">
                  <table class="contacts-table">
                    <thead>
                      <tr>
                        <th class="checkbox-header">
                          <input 
                            type="checkbox" 
                            :checked="areAllContactsSelectedForGroup(group.siteContacts)"
                            @change="toggleAllContactsForGroup(group.siteContacts, $event.target.checked)"
                          />
                        </th>
                        <th v-for="column in getTableColumns('sites')" :key="column.key">{{ column.label }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="contact in group.siteContacts" :key="contact.uuid" class="contact-row">
                        <td class="checkbox-cell">
                          <input 
                            type="checkbox" 
                            :checked="isContactSelected(contact.uuid)"
                            @change="toggleContactSelection(contact.uuid, $event.target.checked)"
                          />
                        </td>
                        <td v-for="column in getTableColumns('sites')" :key="column.key">
                          {{ getContactValue(contact, column.key) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </div>
        </template>

        <!-- Site Contacts Table (for sites without head office or when filtering by sites) -->
        <template v-if="groupedContacts.sites && groupedContacts.sites.length > 0">
          <div class="contact-table-section">
            <h4 class="section-title">Site Contacts</h4>
            <div class="table-container">
              <table class="contacts-table">
                <thead>
                  <tr>
                    <th class="checkbox-header">
                      <input 
                        type="checkbox" 
                        :checked="areAllContactsSelected('sites')"
                        @change="toggleAllContacts('sites', $event.target.checked)"
                      />
                    </th>
                    <th v-for="column in getTableColumns('sites')" :key="column.key">{{ column.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="contact in groupedContacts.sites" :key="contact.uuid" class="contact-row">
                    <td class="checkbox-cell">
                      <input 
                        type="checkbox" 
                        :checked="isContactSelected(contact.uuid)"
                        @change="toggleContactSelection(contact.uuid, $event.target.checked)"
                      />
                    </td>
                    <td v-for="column in getTableColumns('sites')" :key="column.key">
                      {{ getContactValue(contact, column.key) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <!-- No contacts message -->
        <div v-if="filteredContacts.length === 0" class="no-contacts">
          <p>No contacts found for the selected filter.</p>
        </div>
      </div>
      
      <!-- Sync Button -->
      <div class="sync-button-container">
        <!-- Initial Sync Message -->
        <p v-if="!initialSyncAt" class="sync-action-message">
          Click to Perform the Initial Sync and activate auto sync.
        </p>
        <!-- Manual Sync Message -->
        <p v-if="initialSyncAt" class="sync-action-message">
          Click to save auto sync settings and perform manual sync
        </p>
        
        <button 
          class="sync-button" 
          @click="syncContacts"
          :disabled="syncing || (!syncSettings.syncIndividual && !syncSettings.syncCompany && !syncSettings.syncSite && !initialSyncAt)"
        >
          <span v-if="!initialSyncAt">Sync {{ validContactsCount }} Contacts</span>
          <span v-else>Perform Manual sync on {{ validContactsCount }} contacts</span>
        </button>
        <p v-if="excludedContactsCountComputed > 0" class="excluded-contacts-message">
          {{ excludedContactsCountComputed }} Contact{{ excludedContactsCountComputed !== 1 ? 's' : '' }} Excluded
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContactSyncDashboard',
  data() {
    return {
      companies: [],
      companyContacts: [],
      contactsWithCompany: [], // Contacts matched with their company info
      loading: false,
      error: null,
      selectedFilters: [], // Array of selected filters: ['individuals', 'companies', etc.]
      selectedContacts: new Set(), // Set of contact UUIDs that are selected for syncing (all selected by default)
      syncing: false, // Whether sync is in progress
      syncCurrent: 0, // Current contact being synced
      syncTotal: 0, // Total contacts to sync
      syncStatus: '', // Current sync status message
      syncContactName: '', // Name of current contact being synced
      syncErrors: [], // Array of sync errors
      showSyncSummary: false, // Show sync summary modal
      syncId: null, // Active sync ID (polling)
      canceling: false, // Cancellation in progress
      syncSeq: 0, // Last processed progress event sequence
      syncSummary: { // Sync summary data
        synced: 0,
        errors: 0,
        excluded: 0,
        excludedContacts: []
      },
      metrics: {
        companies: 0,
        headOffices: 0,
        sites: 0,
        individuals: 0,
        companyContacts: 0,
        headOfficeContacts: 0,
        siteContacts: 0,
        individualContacts: 0,
      },
      tableColumns: {
        individuals: [
          { key: 'first', label: 'First Name' },
          { key: 'last', label: 'Last Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'mobile', label: 'Mobile' },
          { key: 'type', label: 'Contact Type' },
          { key: 'synced', label: 'Synced' },
        ],
        companies: [
          { key: 'companyName', label: 'Company Name' },
          { key: 'first', label: 'First Name' },
          { key: 'last', label: 'Last Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'mobile', label: 'Mobile' },
          { key: 'type', label: 'Contact Type' },
          { key: 'synced', label: 'Synced' },
        ],
        headOffices: [
          { key: 'companyName', label: 'Head Office' },
          { key: 'first', label: 'First Name' },
          { key: 'last', label: 'Last Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'mobile', label: 'Mobile' },
          { key: 'type', label: 'Contact Type' },
          { key: 'synced', label: 'Synced' },
        ],
        sites: [
          { key: 'headOfficeName', label: 'Head Office' },
          { key: 'companyName', label: 'Site Name' },
          { key: 'first', label: 'First Name' },
          { key: 'last', label: 'Last Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'mobile', label: 'Mobile' },
          { key: 'type', label: 'Contact Type' },
          { key: 'synced', label: 'Synced' },
        ],
      },
      contactMappings: {}, // Map of servicem8ContactId -> mapping object
      syncSettings: {
        syncIndividual: true,
        syncCompany: true,
        syncSite: true,
      },
      initialSyncAt: null, // Timestamp when initial sync was completed
    }
  },
  computed: {
    totalActiveClients() {
      return this.companies.length
    },
    totalContacts() {
      return this.companyContacts.length
    },
    isTotalActiveClientsActive() {
      // Active when no sub-cards are selected OR when all 4 sub-cards are selected
      const allSubCards = ['individuals', 'companies', 'headOffices', 'sites']
      const allSelected = allSubCards.every(card => this.selectedFilters.includes(card))
      return this.selectedFilters.length === 0 || allSelected
    },
    selectedContactsCount() {
      // If filters are selected, only count contacts that match the filters AND are selected
      if (this.selectedFilters.length > 0) {
        return this.filteredContacts.filter(contact => 
          this.selectedContacts.has(contact.uuid)
        ).length
      }
      // If no filters selected, count all selected contacts
      return this.selectedContacts.size
    },
    progressPercentage() {
      if (this.syncTotal === 0) return 0
      return Math.round((this.syncCurrent / this.syncTotal) * 100)
    },
    validContactsCount() {
      // Count contacts that have at least one of: email, phone, firstName, or lastName
      const selectedContactsList = this.filteredContacts.filter(contact => 
        this.selectedContacts.has(contact.uuid)
      );
      
      const validCount = selectedContactsList.filter(contact => {
        const hasEmail = contact.email && contact.email.trim() !== '';
        const hasPhone = (contact.phone && contact.phone.trim() !== '') || (contact.mobile && contact.mobile.trim() !== '');
        const hasFirstName = contact.first && contact.first.trim() !== '';
        const hasLastName = contact.last && contact.last.trim() !== '';
        
        return hasEmail || hasPhone || hasFirstName || hasLastName;
      }).length;
      
      return validCount;
    },
    excludedContactsCountComputed() {
      // Calculate excluded contacts count (without side effects)
      const selectedContactsList = this.filteredContacts.filter(contact => 
        this.selectedContacts.has(contact.uuid)
      );
      
      const validCount = selectedContactsList.filter(contact => {
        const hasEmail = contact.email && contact.email.trim() !== '';
        const hasPhone = (contact.phone && contact.phone.trim() !== '') || (contact.mobile && contact.mobile.trim() !== '');
        const hasFirstName = contact.first && contact.first.trim() !== '';
        const hasLastName = contact.last && contact.last.trim() !== '';
        
        return hasEmail || hasPhone || hasFirstName || hasLastName;
      }).length;
      
      return selectedContactsList.length - validCount;
    },
    filteredContacts() {
      // If no filters selected, return all contacts
      if (this.selectedFilters.length === 0 || (this.selectedFilters.length === 1 && this.selectedFilters[0] === 'all')) {
        return this.contactsWithCompany
      }
      
      // Filter by selected types
      return this.contactsWithCompany.filter(contact => {
        return this.selectedFilters.includes(contact.companyType)
      })
    },
    groupedContacts() {
      // New order: individuals, companies, headOffices (with sites nested), sites
      const individuals = []
      const companies = []
      const headOfficesGrouped = {} // Will contain headOfficeUuid -> { headOffice, headOfficeContacts: [], siteContacts: [] }
      const sites = []

      // First, separate contacts by type
      this.filteredContacts.forEach(contact => {
        const type = contact.companyType || 'unknown'
        
        if (type === 'individuals') {
          individuals.push(contact)
        } else if (type === 'companies') {
          companies.push(contact)
        } else if (type === 'headOffices') {
          // Group head office contacts by their head office UUID
          const uuid = contact.company_uuid
          if (!headOfficesGrouped[uuid]) {
            headOfficesGrouped[uuid] = {
              headOffice: contact.company,
              headOfficeName: contact.companyName,
              headOfficeUuid: uuid,
              headOfficeContacts: [],
              siteContacts: [],
            }
          }
          headOfficesGrouped[uuid].headOfficeContacts.push(contact)
        } else if (type === 'sites') {
          // Group site contacts under their head office
          if (contact.headOfficeUuid && headOfficesGrouped[contact.headOfficeUuid]) {
            headOfficesGrouped[contact.headOfficeUuid].siteContacts.push(contact)
          } else {
            // Site without a head office (shouldn't happen, but handle it)
            sites.push(contact)
          }
        }
      })

      // Sort each group
      individuals.sort((a, b) => {
        const aName = `${a.first || ''} ${a.last || ''}`.trim()
        const bName = `${b.first || ''} ${b.last || ''}`.trim()
        return aName.localeCompare(bName)
      })

      companies.sort((a, b) => {
        if (a.companyName !== b.companyName) {
          return a.companyName.localeCompare(b.companyName)
        }
        const aName = `${a.first || ''} ${a.last || ''}`.trim()
        const bName = `${b.first || ''} ${b.last || ''}`.trim()
        return aName.localeCompare(bName)
      })

      // Sort head office contacts and site contacts within each head office group
      Object.values(headOfficesGrouped).forEach(group => {
        group.headOfficeContacts.sort((a, b) => {
          const aName = `${a.first || ''} ${a.last || ''}`.trim()
          const bName = `${b.first || ''} ${b.last || ''}`.trim()
          return aName.localeCompare(bName)
        })
        group.siteContacts.sort((a, b) => {
          if (a.companyName !== b.companyName) {
            return a.companyName.localeCompare(b.companyName)
          }
          const aName = `${a.first || ''} ${a.last || ''}`.trim()
          const bName = `${b.first || ''} ${b.last || ''}`.trim()
          return aName.localeCompare(bName)
        })
      })

      sites.sort((a, b) => {
        if (a.companyName !== b.companyName) {
          return a.companyName.localeCompare(b.companyName)
        }
        const aName = `${a.first || ''} ${a.last || ''}`.trim()
        const bName = `${b.first || ''} ${b.last || ''}`.trim()
        return aName.localeCompare(bName)
      })

      // Build result in correct order
      const result = {}
      if (individuals.length > 0) {
        result.individuals = individuals
      }
      if (companies.length > 0) {
        result.companies = companies
      }
      if (Object.keys(headOfficesGrouped).length > 0) {
        result.headOfficesGrouped = headOfficesGrouped
      }
      if (sites.length > 0) {
        result.sites = sites
      }

      return result
    },
  },
  async mounted() {
    await this.loadSyncSettings()
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.error = null

      try {
        const ssoToken = await this.getSSOToken()
        const headers = { 'x-sso-token': ssoToken }

        // Fetch companies and contacts in parallel
        const [companiesResponse, contactsResponse] = await Promise.all([
          fetch('/servicem8/companies?filter=active eq 1', { headers }),
          fetch('/servicem8/company-contacts?filter=active eq 1', { headers }),
        ])

        if (!companiesResponse.ok) {
          const errorData = await companiesResponse.json().catch(() => ({ error: 'Unknown error' }))
          throw new Error(errorData.error || 'Failed to load active clients')
        }

        if (!contactsResponse.ok) {
          const errorData = await contactsResponse.json().catch(() => ({ error: 'Unknown error' }))
          throw new Error(errorData.error || 'Failed to load company contacts')
        }

        const companiesData = await companiesResponse.json()
        const contactsData = await contactsResponse.json()

        this.companies = Array.isArray(companiesData) ? companiesData : []
        this.companyContacts = Array.isArray(contactsData) ? contactsData : []

        // Load contact mappings to check sync status
        await this.loadContactMappings()

        // Match contacts to companies and calculate metrics
        this.matchContactsToCompanies()
        this.calculateMetrics()
        
        // Initialize all contacts as selected by default
        this.initializeSelectedContacts()
      } catch (error) {
        console.error('Error loading data:', error)
        this.error = error.message || 'Failed to load data'
      } finally {
        this.loading = false
      }
    },
    matchContactsToCompanies() {
      // Create a map of company UUIDs to company objects for fast lookup
      const companyMap = new Map()
      this.companies.forEach(company => {
        companyMap.set(company.uuid, company)
      })

      // Match contacts to companies and determine company type
      this.contactsWithCompany = this.companyContacts.map(contact => {
        const company = companyMap.get(contact.company_uuid)
        let companyType = 'unknown'
        let companyName = 'Unknown'
        let headOfficeName = null
        let headOfficeUuid = null

        if (company) {
          companyName = company.name || 'Unknown'

          // Determine company type
          if (company.is_individual === 1 || company.is_individual === '1') {
            companyType = 'individuals'
          } else if (company.parent_company_uuid && company.parent_company_uuid.trim() !== '') {
            companyType = 'sites'
            // Get head office name for site contacts
            const headOffice = companyMap.get(company.parent_company_uuid)
            if (headOffice) {
              headOfficeName = headOffice.name || 'Unknown'
              headOfficeUuid = headOffice.uuid
            }
          } else {
            // Check if this company UUID is referenced as a parent (head office)
            const isHeadOffice = this.companies.some(c => 
              c.parent_company_uuid === company.uuid
            )
            if (isHeadOffice) {
              companyType = 'headOffices'
            } else {
              companyType = 'companies'
            }
          }
        }

        return {
          ...contact,
          company,
          companyName,
          companyType,
          headOfficeName,
          headOfficeUuid,
        }
      })

      console.log('Matched contacts to companies:', this.contactsWithCompany.length)
    },
    calculateMetrics() {
      // Reset metrics
      this.metrics = {
        companies: 0,
        headOffices: 0,
        sites: 0,
        individuals: 0,
        companyContacts: 0,
        headOfficeContacts: 0,
        siteContacts: 0,
        individualContacts: 0,
      }

      // First pass: collect all parent_company_uuid values to identify head offices
      const headOfficeUuids = new Set()
      this.companies.forEach((company) => {
        if (company.parent_company_uuid && company.parent_company_uuid.trim() !== '') {
          headOfficeUuids.add(company.parent_company_uuid)
        }
      })

      // Second pass: categorize each company
      this.companies.forEach((company) => {
        // Check if individual
        if (company.is_individual === 1 || company.is_individual === '1') {
          this.metrics.individuals++
          return
        }

        // Check if has parent company (site)
        if (company.parent_company_uuid && company.parent_company_uuid.trim() !== '') {
          this.metrics.sites++
          return
        }

        // If no parent company, check if this UUID is referenced as a parent (head office)
        if (headOfficeUuids.has(company.uuid)) {
          this.metrics.headOffices++
          return
        }

        // Otherwise it's a regular company
        this.metrics.companies++
      })

      // Count contacts by type
      this.contactsWithCompany.forEach(contact => {
        switch (contact.companyType) {
          case 'companies':
            this.metrics.companyContacts++
            break
          case 'headOffices':
            this.metrics.headOfficeContacts++
            break
          case 'sites':
            this.metrics.siteContacts++
            break
          case 'individuals':
            this.metrics.individualContacts++
            break
        }
      })

      console.log('Calculated metrics:', this.metrics)
    },
    selectFilter(filter) {
      // When clicking "Total Active Clients", select all filters and checkboxes
      if (filter === 'all') {
        this.selectedFilters = []
        // Select all checkboxes (only during initial sync)
        if (!this.initialSyncAt) {
          this.syncSettings.syncIndividual = true
          this.syncSettings.syncCompany = true
          this.syncSettings.syncSite = true
        }
      } else {
        // This shouldn't happen with 'all', but handle it
        this.selectedFilters = [filter]
      }
    },
    toggleFilter(filter) {
      // Toggle filter in the array
      const index = this.selectedFilters.indexOf(filter)
      if (index > -1) {
        // Remove if already selected
        this.selectedFilters.splice(index, 1)
        
        // If sites is being deselected, also deselect head offices
        if (filter === 'sites') {
          const headOfficesIndex = this.selectedFilters.indexOf('headOffices')
          if (headOfficesIndex > -1) {
            this.selectedFilters.splice(headOfficesIndex, 1)
          }
        }
        
        // If head offices is being deselected, also deselect sites (since sites depend on head offices)
        if (filter === 'headOffices') {
          const sitesIndex = this.selectedFilters.indexOf('sites')
          if (sitesIndex > -1) {
            this.selectedFilters.splice(sitesIndex, 1)
          }
        }
      } else {
        // Add if not selected
        this.selectedFilters.push(filter)
        
        // If sites is being selected, also select head offices (since sites are nested under head offices)
        if (filter === 'sites') {
          const headOfficesIndex = this.selectedFilters.indexOf('headOffices')
          if (headOfficesIndex === -1) {
            this.selectedFilters.push('headOffices')
          }
        }
      }
      // Ensure 'all' is not in the array when other filters are selected
      const allIndex = this.selectedFilters.indexOf('all')
      if (allIndex > -1 && this.selectedFilters.length > 1) {
        this.selectedFilters.splice(allIndex, 1)
      }
      
      // Update sync settings based on filter selections (only during initial sync)
      if (!this.initialSyncAt) {
        this.updateSyncSettingsFromFilters()
      }
    },
    updateSyncSettingsFromFilters() {
      // Update sync settings checkboxes based on selected filter cards
      this.syncSettings.syncIndividual = this.selectedFilters.includes('individuals')
      this.syncSettings.syncCompany = this.selectedFilters.includes('companies')
      // Sites checkbox should be checked if either 'sites' or 'headOffices' is selected
      this.syncSettings.syncSite = this.selectedFilters.includes('sites') || this.selectedFilters.includes('headOffices')
    },
    updateSyncSettingsFromCheckbox(filterCard, checked) {
      // Update filter cards based on checkbox selection (only during initial sync)
      if (!this.initialSyncAt) {
        if (checked) {
          // Add filter if not already selected
          if (!this.selectedFilters.includes(filterCard)) {
            this.selectedFilters.push(filterCard)
            
            // If sites is being selected, also select head offices
            if (filterCard === 'sites') {
              const headOfficesIndex = this.selectedFilters.indexOf('headOffices')
              if (headOfficesIndex === -1) {
                this.selectedFilters.push('headOffices')
              }
            }
          }
        } else {
          // Remove filter if selected
          const index = this.selectedFilters.indexOf(filterCard)
          if (index > -1) {
            this.selectedFilters.splice(index, 1)
          }
          
          // If sites is being deselected, also deselect head offices
          if (filterCard === 'sites') {
            const headOfficesIndex = this.selectedFilters.indexOf('headOffices')
            if (headOfficesIndex > -1) {
              this.selectedFilters.splice(headOfficesIndex, 1)
            }
          }
        }
        
        // Ensure 'all' is not in the array when other filters are selected
        const allIndex = this.selectedFilters.indexOf('all')
        if (allIndex > -1 && this.selectedFilters.length > 1) {
          this.selectedFilters.splice(allIndex, 1)
        }
      }
    },
    initializeSelectedContacts() {
      // Initialize all contacts as selected (checked by default)
      this.selectedContacts = new Set(this.contactsWithCompany.map(contact => contact.uuid))
    },
    isContactSelected(contactUuid) {
      return this.selectedContacts.has(contactUuid)
    },
    toggleContactSelection(contactUuid, checked) {
      if (checked) {
        this.selectedContacts.add(contactUuid)
      } else {
        this.selectedContacts.delete(contactUuid)
      }
    },
    areAllContactsSelected(type) {
      // Check if all contacts of a specific type are selected
      const contacts = this.getContactsByType(type)
      if (contacts.length === 0) return false
      return contacts.every(contact => this.selectedContacts.has(contact.uuid))
    },
    toggleAllContacts(type, checked) {
      // Toggle all contacts of a specific type
      const contacts = this.getContactsByType(type)
      contacts.forEach(contact => {
        if (checked) {
          this.selectedContacts.add(contact.uuid)
        } else {
          this.selectedContacts.delete(contact.uuid)
        }
      })
    },
    areAllContactsSelectedForGroup(contacts) {
      // Check if all contacts in a group are selected
      if (contacts.length === 0) return false
      return contacts.every(contact => this.selectedContacts.has(contact.uuid))
    },
    toggleAllContactsForGroup(contacts, checked) {
      // Toggle all contacts in a group
      contacts.forEach(contact => {
        if (checked) {
          this.selectedContacts.add(contact.uuid)
        } else {
          this.selectedContacts.delete(contact.uuid)
        }
      })
    },
    getContactsByType(type) {
      // Get all contacts of a specific type from groupedContacts
      switch (type) {
        case 'individuals':
          return this.groupedContacts.individuals || []
        case 'companies':
          return this.groupedContacts.companies || []
        case 'sites':
          return this.groupedContacts.sites || []
        default:
          return []
      }
    },
    getTableTitle() {
      if (this.selectedFilters.length === 0) {
        return 'All Contacts (Sorted by Type)'
      }
      if (this.selectedFilters.length === 1) {
        switch (this.selectedFilters[0]) {
          case 'companies':
            return 'Company Contacts'
          case 'headOffices':
            return 'Head Office Contacts'
          case 'sites':
            return 'Site Contacts'
          case 'individuals':
            return 'Individual Contacts'
          default:
            return 'All Contacts (Sorted by Type)'
        }
      }
      // Multiple filters selected
      return `${this.selectedFilters.length} Types Selected`
    },
    getTypeLabel(type) {
      const typeMap = {
        companies: 'Company Contacts',
        headOffices: 'Head Office Contacts',
        sites: 'Site Contacts',
        individuals: 'Individual Contacts',
      }
      return typeMap[type] || type
    },
    getTableColumns(type) {
      return this.tableColumns[type] || this.tableColumns.companies
    },
    async loadContactMappings() {
      try {
        const ssoToken = await this.getSSOToken()
        const headers = { 'x-sso-token': ssoToken }
        
        const response = await fetch('/servicem8/sync/contact-mappings', { headers })
        if (response.ok) {
          const mappings = await response.json()
          // Create a map of servicem8ContactId -> mapping
          this.contactMappings = {}
          if (Array.isArray(mappings)) {
            mappings.forEach(mapping => {
              if (mapping.servicem8ContactId) {
                this.contactMappings[mapping.servicem8ContactId] = mapping
              }
            })
          }
          console.log(`[ContactSyncDashboard] Loaded ${Object.keys(this.contactMappings).length} contact mappings`)
        }
      } catch (error) {
        console.error('Error loading contact mappings:', error)
      }
    },
    getContactValue(contact, key) {
      // Handle synced column specially
      if (key === 'synced') {
        const mapping = this.contactMappings[contact.uuid]
        return mapping ? '✓' : ''
      }
      
      const value = contact[key]
      if (value === null || value === undefined || value === '') {
        return '—'
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
      }
      return String(value)
    },
    async syncContacts() {
      // Only sync contacts that are both filtered (visible) and selected
      const selectedContactsList = this.filteredContacts.filter(contact => 
        this.selectedContacts.has(contact.uuid)
      );
      
      // Filter out contacts that don't have at least one of: email, phone, firstName, or lastName
      const validContacts = selectedContactsList.filter(contact => {
        const hasEmail = contact.email && contact.email.trim() !== '';
        const hasPhone = (contact.phone && contact.phone.trim() !== '') || (contact.mobile && contact.mobile.trim() !== '');
        const hasFirstName = contact.first && contact.first.trim() !== '';
        const hasLastName = contact.last && contact.last.trim() !== '';
        
        return hasEmail || hasPhone || hasFirstName || hasLastName;
      });
      
      const validUuids = validContacts.map(contact => contact.uuid);
      const excludedCount = selectedContactsList.length - validContacts.length;
      
      if (validUuids.length === 0) {
        if (excludedCount > 0) {
          alert(`All selected contacts are excluded. Contacts must have at least one of: email, phone, firstName, or lastName.`)
        } else {
          alert('Please select at least one contact to sync')
        }
        return
      }

      // Initialize sync progress
      this.syncing = true
      this.syncCurrent = 0
      this.syncTotal = validUuids.length
      this.syncStatus = 'Starting sync...'
      this.syncContactName = ''
      this.syncErrors = []
      this.showSyncSummary = false // Ensure modal is hidden during sync
      this.syncId = null
      this.canceling = false
      this.syncSeq = 0
      
      // Force Vue to render the progress bar immediately
      await this.$nextTick()

      try {
        const ssoToken = await this.getSSOToken()
        
        console.log(`[ContactSyncDashboard] Syncing ${validUuids.length} valid contacts (${excludedCount} excluded):`, validUuids)
        
        // Start sync and poll for progress updates
        const response = await fetch('/servicem8/sync/selected-contacts', {
          method: 'POST',
          headers: {
            'x-sso-token': ssoToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            contactUuids: validUuids,
            streamProgress: true,
            syncSettings: this.syncSettings,
            isInitialSync: !this.initialSyncAt
          })
        })

        if (!response.ok) {
          const errorData = await response.text().catch(() => 'Unknown error')
          throw new Error(errorData || 'Failed to sync contacts')
        }

        const { syncId } = await response.json()
        if (!syncId) {
          throw new Error('Missing syncId from server')
        }
        this.syncId = syncId

        let polling = true
        const pollIntervalMs = 400
        while (polling) {
          const progressResponse = await fetch(`/servicem8/sync/selected-contacts/progress/${syncId}?since=${this.syncSeq}`, {
            headers: { 'x-sso-token': ssoToken },
            cache: 'no-store'
          })

          if (!progressResponse.ok) {
            throw new Error('Failed to fetch sync progress')
          }

          const payload = await progressResponse.json()
          const events = Array.isArray(payload.events) ? payload.events : []
          this.syncSeq = payload.latestSeq || this.syncSeq

          for (const data of events) {
            console.log(`[Frontend Progress] Received update: ${data.current}/${data.total} - ${data.status}`)

            if (data.current !== undefined) {
              this.syncCurrent = data.current
            }
            if (data.total !== undefined) {
              this.syncTotal = data.total
            }
            if (data.status) {
              this.syncStatus = data.status
            }
            if (data.contactName !== undefined) {
              this.syncContactName = data.contactName || ''
            }

            if (data.error) {
              this.syncErrors.push(`${data.contactName || 'Contact'}: ${data.error}`)
            }

            await this.$nextTick()
            this.$forceUpdate()

            if (data.type === 'complete') {
              this.syncCurrent = this.syncTotal
              this.syncStatus = `Sync complete: ${data.synced} successful, ${data.errors} failed`
              this.syncContactName = ''
              this.syncId = null
              this.canceling = false
              this.syncSummary = {
                synced: data.synced || 0,
                errors: data.errors || 0,
                excluded: data.excluded || 0,
                excludedContacts: data.excludedContacts || []
              }
              await this.$nextTick()
              this.$forceUpdate()
              // Reload sync settings to get updated initialSyncAt
              await this.loadSyncSettings()
              await this.loadData()
              setTimeout(() => {
                this.syncing = false
                this.showSyncSummary = true
              }, 1500)
              polling = false
              break
            } else if (data.type === 'error') {
              this.syncErrors.push(data.error)
              this.syncStatus = 'Sync failed: ' + data.error
              this.syncing = false
              this.syncId = null
              this.canceling = false
              polling = false
              break
            } else if (data.type === 'cancelled') {
              this.syncStatus = 'Sync cancelled'
              this.syncing = false
              this.syncId = null
              this.canceling = false
              polling = false
              break
            }
          }

          if (polling) {
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs))
          }
        }
      } catch (error) {
        console.error('Error syncing contacts:', error)
        this.syncErrors.push(error.message || 'Unknown error')
        this.syncStatus = 'Sync failed: ' + (error.message || 'Unknown error')
        this.syncId = null
        this.canceling = false
        
        // Show summary modal with error
        this.syncSummary = {
          synced: this.syncCurrent,
          errors: 1,
          excluded: 0,
          excludedContacts: []
        }
        this.syncing = false
        this.showSyncSummary = true
      }
    },
    closeSyncSummary() {
      this.showSyncSummary = false
      this.syncErrors = []
      this.syncSummary = {
        synced: 0,
        errors: 0,
        excluded: 0,
        excludedContacts: []
      }
    },
    cancelSync() {
      if (this.canceling) {
        return
      }

      if (!this.syncId) {
        this.syncing = false
        this.syncStatus = 'Sync cancelled'
        return
      }

      this.canceling = true
      this.syncStatus = 'Cancelling sync...'

      this.getSSOToken()
        .then((ssoToken) => fetch(`/servicem8/sync/selected-contacts/cancel/${this.syncId}`, {
          method: 'POST',
          headers: { 'x-sso-token': ssoToken }
        }))
        .catch((error) => {
          console.error('Error cancelling sync:', error)
          this.syncStatus = 'Failed to cancel sync'
          this.canceling = false
        })
    },
    async loadSyncSettings() {
      try {
        const ssoToken = await this.getSSOToken()
        const response = await fetch('/servicem8/sync/settings', {
          headers: { 'x-sso-token': ssoToken }
        })
        
        if (response.ok) {
          const data = await response.json()
          this.syncSettings = {
            syncIndividual: data.syncIndividual !== false, // Default to true
            syncCompany: data.syncCompany !== false, // Default to true
            syncSite: data.syncSite !== false, // Default to true
          }
          this.initialSyncAt = data.initialSyncAt || null
          console.log('[ContactSyncDashboard] Loaded sync settings:', this.syncSettings, 'initialSyncAt:', this.initialSyncAt)
        }
      } catch (error) {
        console.error('Error loading sync settings:', error)
        // Use defaults if loading fails
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
  },
}
</script>

<style scoped>
.contact-sync-dashboard {
  width: 100%;
}

.loading,
.error {
  padding: 40px;
  text-align: center;
  color: #666;
}

.error {
  color: #d32f2f;
}

.error p {
  margin-bottom: 16px;
}

.btn-retry {
  padding: 10px 20px;
  background: #24ACFE;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-retry:hover {
  background: #1a8fd8;
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.total-count-card {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  color: #333;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s;
}

.total-count-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  border-color: #24ACFE;
}

.total-count-card.active {
  background: linear-gradient(135deg, #24ACFE 0%, #1a8fd8 100%);
  border-color: white;
  color: white;
  box-shadow: 0 6px 16px rgba(36, 172, 254, 0.4);
}

.total-count-card.active .total-count-label,
.total-count-card.active .total-count-value,
.total-count-card.active .contact-count {
  color: white;
}

.total-count-label {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  opacity: 0.9;
}

.total-count-value {
  font-size: 48px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 8px;
}

.contact-count {
  font-size: 14px;
  opacity: 0.9;
  margin-top: 8px;
}

.breakdown-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.metric-card {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
}

.metric-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: #24ACFE;
}

.metric-card.active {
  background: linear-gradient(135deg, #24ACFE 0%, #1a8fd8 100%);
  border-color: #24ACFE;
  color: white;
  box-shadow: 0 6px 16px rgba(36, 172, 254, 0.3);
}

.metric-card.active .metric-label,
.metric-card.active .metric-value,
.metric-card.active .contact-count {
  color: white;
}

.metric-label {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-card.active .metric-label {
  opacity: 0.9;
}

.metric-value {
  font-size: 36px;
  font-weight: 700;
  color: #333;
  line-height: 1;
  margin-bottom: 8px;
}

.contact-count {
  font-size: 13px;
  color: #666;
  margin-top: 8px;
}

.contacts-tables-section {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.contact-table-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.section-title {
  margin: 0;
  padding: 20px 24px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.nested-table-container {
  margin-top: 24px;
  padding: 0 24px 24px;
}

.nested-section-title {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #555;
}

.table-container {
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
}

.contacts-table {
  width: 100%;
  border-collapse: collapse;
}

.contacts-table thead {
  background: #f5f5f5;
  position: sticky;
  top: 0;
  z-index: 10;
}

.contacts-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #666;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}

.contacts-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #333;
}

.contacts-table td:last-child {
  text-align: center;
  font-size: 18px;
  color: #4caf50;
  font-weight: bold;
}

.contacts-table tbody tr:hover {
  background: #fafafa;
}

.type-header-row {
  background: #f5f5f5 !important;
}

.type-header-row:hover {
  background: #f5f5f5 !important;
}

.type-header-cell {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  padding: 16px;
  background: #e8f4fd;
  border-top: 2px solid #24ACFE;
  border-bottom: 1px solid #e0e0e0;
}

.contact-row:hover {
  background: #fafafa;
}

.checkbox-header {
  width: 40px;
  text-align: center;
  padding: 12px 8px !important;
}

.checkbox-header input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-cell {
  width: 40px;
  text-align: center;
  padding: 12px 8px !important;
}

.checkbox-cell input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.no-contacts {
  padding: 40px;
  text-align: center;
  color: #999;
}

.sync-settings-container {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.sync-settings-container h4 {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.sync-settings-description {
  margin: 0 0 15px 0;
  color: #666;
  font-size: 14px;
}

.sync-settings-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sync-setting-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.sync-setting-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.sync-setting-checkbox span {
  font-size: 14px;
  color: #333;
}

.sync-setting-checkbox:has(input:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
}

.sync-settings-warning {
  margin: 15px 0 0 0;
  padding: 10px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 14px;
}

.sync-button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  background: white;
  border-top: 1px solid #e0e0e0;
  margin-top: 24px;
}

.sync-button {
  padding: 14px 32px;
  background: linear-gradient(135deg, #24ACFE 0%, #1a8fd8 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 200px;
}

.sync-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(36, 172, 254, 0.3);
  background: linear-gradient(135deg, #1a8fd8 0%, #1580c0 100%);
}

.sync-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.sync-action-message {
  margin: 0 0 12px 0;
  padding: 12px 16px;
  background: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 6px;
  color: #1565c0;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.excluded-contacts-message {
  margin: 8px 0 0 0;
  font-size: 13px;
  color: #f44336;
  font-weight: 500;
  text-align: center;
}

.sync-progress-container {
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 24px 0;
  position: relative;
  z-index: 100;
}

.sync-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.sync-progress-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.btn-cancel {
  padding: 8px 16px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-cancel:hover {
  background: #d32f2f;
}

.sync-progress-bar-container {
  width: 100%;
  height: 24px;
  background: #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  position: relative;
}

.sync-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #24ACFE 0%, #1a8fd8 100%);
  border-radius: 12px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.sync-progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.sync-progress-info {
  margin-bottom: 16px;
}

.sync-status {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 8px 0;
}

.sync-details {
  font-size: 14px;
  color: #666;
  margin: 0 0 4px 0;
}

.sync-contact-name {
  font-size: 15px;
  color: #24ACFE;
  margin: 12px 0 0 0;
  font-weight: 500;
  padding: 10px 14px;
  background: #f0f8ff;
  border-radius: 6px;
  border-left: 3px solid #24ACFE;
}

.sync-errors {
  margin-top: 20px;
  padding: 16px;
  background: #ffebee;
  border-radius: 8px;
  border-left: 4px solid #f44336;
}

.sync-errors h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #c62828;
}

.sync-errors ul {
  margin: 0;
  padding-left: 20px;
}

.sync-errors li {
  font-size: 13px;
  color: #d32f2f;
  margin-bottom: 4px;
}

@media (max-width: 768px) {
  .breakdown-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .total-count-value {
    font-size: 36px;
  }

  .metric-value {
    font-size: 28px;
  }

  .table-container {
    max-height: 400px;
  }

  .sync-progress-container {
    padding: 20px;
  }
}

/* Sync Summary Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.sync-summary-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 32px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.modal-close:hover {
  color: #333;
}

.modal-body {
  padding: 24px;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.summary-stat {
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.summary-stat.success {
  background: #e8f5e9;
  border: 2px solid #4caf50;
}

.summary-stat.error {
  background: #ffebee;
  border: 2px solid #f44336;
}

.summary-stat.excluded {
  background: #fff3e0;
  border: 2px solid #ff9800;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #333;
}

.summary-stat.success .stat-value {
  color: #2e7d32;
}

.summary-stat.error .stat-value {
  color: #c62828;
}

.summary-stat.excluded .stat-value {
  color: #e65100;
}

.summary-errors,
.summary-excluded {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.summary-errors h3,
.summary-excluded h3 {
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.summary-errors ul {
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
}

.summary-errors li {
  margin: 8px 0;
  color: #c62828;
  font-size: 14px;
}

.summary-excluded p {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

.btn-primary {
  padding: 10px 24px;
  background: linear-gradient(135deg, #24ACFE 0%, #1a8fd8 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #1a8fd8 0%, #1580c0 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(36, 172, 254, 0.3);
}
</style>
